import React, { useState } from "react";  
import { useNavigate } from "react-router-dom";  
import api from "../api";  
import Navbar from "./Navbar";  
import { motion, AnimatePresence } from "framer-motion";  
  
const AddIG = () => {  
  const navigate = useNavigate();  
  const [formData, setFormData] = useState({  
    api_key: "",  
    username: "",  
    password: "",  
    demo: true,  
  });  
  const [loading, setLoading] = useState(false);  
  const [notification, setNotification] = useState(null);  
  const [showPassword, setShowPassword] = useState(false);  
  
  const showNotification = (message, type = "info") => {  
    setNotification({ message, type });  
    setTimeout(() => setNotification(null), 4500);  
  };  
  
  const handleSubmit = async (e) => {  
    e.preventDefault();  
    setLoading(true);  
    try {  
      const response = await api.post("/users/ig/connect", formData);  
      if (response.data.success) {  
        showNotification("IG Markets connected successfully", "success");  
        setTimeout(() => navigate("/accounts"), 1500);  
      } else {  
        showNotification(response.data.message || "Failed to connect", "error");  
      }  
    } catch (error) {  
      console.error("IG connection error:", error);  
      let errorMessage = "Failed to connect to IG Markets";  
      if (error.response?.data?.detail) {  
        if (Array.isArray(error.response.data.detail)) {  
          errorMessage = error.response.data.detail.map((err) => err.msg).join(", ");  
        } else if (typeof error.response.data.detail === "object") {  
          errorMessage = JSON.stringify(error.response.data.detail);  
        } else {  
          errorMessage = error.response.data.detail;  
        }  
      }  
      showNotification(errorMessage, "error");  
    } finally {  
      setLoading(false);  
    }  
  };  
  
  const update = (patch) => setFormData((f) => ({ ...f, ...patch }));  
  
  return (  
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black text-white">  
      <Navbar />  
  
      {/* Notification */}  
      <AnimatePresence>  
        {notification && (  
          <motion.div  
            initial={{ opacity: 0, y: -16 }}  
            animate={{ opacity: 1, y: 0 }}  
            exit={{ opacity: 0, y: -16 }}  
            className={`fixed top-20 left-4 right-4 sm:left-auto sm:right-6 z-50 px-5 py-3.5 rounded-xl shadow-2xl border backdrop-blur-md sm:max-w-md ${  
              notification.type === "success"  
                ? "bg-green-900/90 border-green-500/60"  
                : notification.type === "error"  
                ? "bg-red-900/90 border-red-500/60"  
                : "bg-gray-900/90 border-gold/60"  
            }`}  
          >  
            <div className="flex items-center gap-3">  
              <div  
                className={`w-2 h-2 rounded-full animate-pulse ${  
                  notification.type === "success"  
                    ? "bg-green-400"  
                    : notification.type === "error"  
                    ? "bg-red-400"  
                    : "bg-gold"  
                }`}  
              />  
              <p className="text-white text-sm font-medium">{notification.message}</p>  
            </div>  
          </motion.div>  
        )}  
      </AnimatePresence>  
  
      <div className="max-w-2xl mx-auto py-6 sm:py-10 px-4 sm:px-6 lg:px-8">  
        {/* Header */}  
        <motion.div  
          initial={{ opacity: 0, y: 12 }}  
          animate={{ opacity: 1, y: 0 }}  
          className="mb-6 sm:mb-8"  
        >  
          <button  
            onClick={() => navigate("/accounts")}  
            className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gold mb-4 transition-colors"  
          >  
            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">  
              <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />  
            </svg>  
            Back to accounts  
          </button>  
  
          <div className="flex items-center gap-3">  
            <div className="w-1 h-10 bg-gradient-to-b from-gold to-yellow-600 rounded-full" />  
            <div>  
              <h1 className="text-2xl sm:text-3xl font-bold text-white leading-tight">  
                Connect IG Markets  
              </h1>  
              <p className="text-gray-400 text-sm mt-1">  
                Add your credentials to enable Gold & CFD trading  
              </p>  
            </div>  
          </div>  
        </motion.div>  
  
        {/* Form Card */}  
        <motion.div  
          initial={{ opacity: 0, y: 12 }}  
          animate={{ opacity: 1, y: 0 }}  
          transition={{ delay: 0.05 }}  
          className="bg-gradient-to-br from-gray-900/60 via-black/60 to-gray-900/60 backdrop-blur-xl rounded-2xl border border-gray-800 p-6 sm:p-7 shadow-2xl"  
        >  
          <form onSubmit={handleSubmit} className="space-y-5">  
            {/* Account type */}  
            <div>  
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">  
                Account type  
              </label>  
              <div className="grid grid-cols-2 gap-2 p-1 bg-black/40 border border-gray-800 rounded-xl">  
                <button  
                  type="button"  
                  onClick={() => update({ demo: true })}  
                  disabled={loading}  
                  className={`py-2.5 rounded-lg text-sm font-semibold transition-all ${  
                    formData.demo  
                      ? "bg-gold/10 text-gold border border-gold/40"  
                      : "text-gray-400 hover:text-white border border-transparent"  
                  }`}  
                >  
                  Demo  
                </button>  
                <button  
                  type="button"  
                  onClick={() => update({ demo: false })}  
                  disabled={loading}  
                  className={`py-2.5 rounded-lg text-sm font-semibold transition-all ${  
                    !formData.demo  
                      ? "bg-red-500/10 text-red-400 border border-red-500/40"  
                      : "text-gray-400 hover:text-white border border-transparent"  
                  }`}  
                >  
                  Live  
                </button>  
              </div>  
              <p className="text-[11px] text-gray-500 mt-2 leading-relaxed">  
                {formData.demo  
                  ? "Practice with IG's demo account — no real funds involved."  
                  : "Live trading uses real money."}  
              </p>  
            </div>  
  
            {/* API Key */}  
            <FormField  
              label="IG API Key"  
              value={formData.api_key}  
              onChange={(v) => update({ api_key: v })}  
              placeholder="Paste your key from the IG Labs portal"  
              disabled={loading}  
              required  
            />  
  
            {/* Username */}  
            <FormField  
              label="IG Username"  
              value={formData.username}  
              onChange={(v) => update({ username: v })}  
              placeholder="Your IG account username"  
              disabled={loading}  
              required  
            />  
  
            {/* Password */}  
            <FormField  
              label="IG Password"  
              value={formData.password}  
              onChange={(v) => update({ password: v })}  
              placeholder="Your IG account password"  
              type={showPassword ? "text" : "password"}  
              disabled={loading}  
              required  
              trailing={  
                <button  
                  type="button"  
                  onClick={() => setShowPassword(!showPassword)}  
                  className="text-gray-500 hover:text-gold transition-colors"  
                  tabIndex={-1}  
                >  
                  {showPassword ? (  
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">  
                      <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />  
                      <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />  
                    </svg>  
                  ) : (  
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">  
                      <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />  
                      <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />  
                    </svg>  
                  )}  
                </button>  
              }  
            />  
  
            {/* Submit */}  
            <div className="flex gap-3 pt-1">  
              <button  
                type="button"  
                onClick={() => navigate("/accounts")}  
                disabled={loading}  
                className="flex-1 py-3 px-4 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] border border-gray-800 hover:border-gray-700 text-gray-300 hover:text-white font-semibold text-sm transition-all"  
              >  
                Cancel  
              </button>  
              <motion.button  
                type="submit"  
                disabled={loading}  
                whileHover={{ scale: loading ? 1 : 1.01 }}  
                whileTap={{ scale: loading ? 1 : 0.99 }}  
                className={`flex-1 py-3 px-4 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 ${  
                  loading  
                    ? "bg-gray-800 text-gray-500 cursor-not-allowed"  
                    : "bg-gradient-to-r from-gold to-yellow-600 text-black hover:from-yellow-500 hover:to-gold shadow-lg shadow-gold/20"  
                }`}  
              >  
                {loading ? (  
                  <>  
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />  
                    Connecting…  
                  </>  
                ) : (  
                  <>  
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">  
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />  
                    </svg>  
                    Connect account  
                  </>  
                )}  
              </motion.button>  
            </div>  
          </form>  
        </motion.div>  
  
        {/* Security + Help */}  
        <motion.div  
          initial={{ opacity: 0 }}  
          animate={{ opacity: 1 }}  
          transition={{ delay: 0.2 }}  
          className="mt-5 space-y-3"  
        >  
          <div className="bg-black/30 border border-gray-800 rounded-xl p-4 backdrop-blur-sm">  
            <div className="flex items-start gap-3">  
              <div className="w-7 h-7 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center justify-center flex-shrink-0">  
                <svg className="w-3.5 h-3.5 text-green-400" fill="currentColor" viewBox="0 0 20 20">  
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />  
                </svg>  
              </div>  
              <p className="text-xs text-gray-400 leading-relaxed">  
                Credentials are encrypted with AES-256 before being stored, and only decrypted in memory when the bot places a trade. You can disconnect any time.  
              </p>  
            </div>  
          </div>  
  
          <div className="text-center text-xs text-gray-500">  
            Don't have an IG account?{" "}  
            <a  
              href="https://www.ig.com"  
              target="_blank"  
              rel="noopener noreferrer"  
              className="text-gold hover:text-yellow-300 transition-colors"  
            >  
              Sign up  
            </a>  
            {" · "}  
            <a  
              href="https://labs.ig.com"  
              target="_blank"  
              rel="noopener noreferrer"  
              className="text-gold hover:text-yellow-300 transition-colors"  
            >  
              Get your API key  
            </a>  
          </div>  
        </motion.div>  
      </div>  
    </div>  
  );  
};  
  
/* ─── Reusable Form Field (same as AddAPI) ─── */  
const FormField = ({  
  label,  
  value,  
  onChange,  
  placeholder,  
  type = "text",  
  disabled,  
  required,  
  trailing,  
  helper,  
}) => (  
  <div>  
    <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">  
      {label}  
    </label>  
    <div className="relative">  
      <input  
        type={type}  
        value={value}  
        onChange={(e) => onChange(e.target.value)}  
        placeholder={placeholder}  
        disabled={disabled}  
        required={required}  
        className={`w-full px-4 py-3 bg-black/40 border border-gray-800 focus:border-gold/50 focus:ring-1 focus:ring-gold/30 rounded-xl text-white text-sm placeholder-gray-600 outline-none transition-all ${  
          trailing ? "pr-11" : ""  
        }`}  
      />  
      {trailing && (  
        <div className="absolute right-3 top-1/2 -translate-y-1/2">{trailing}</div>  
      )}  
    </div>  
    {helper && <p className="text-[11px] text-gray-500 mt-1.5">{helper}</p>}  
  </div>  
);  
  
export default AddIG;  